/**
 * Etailize Field Validation Utilities
 * 
 * Provides validation functions for different types of Etailize authentication fields.
 * Integrates with existing validation patterns and supports async validation for connection testing.
 */

/**
 * Validate a single field based on its definition and value
 * @param {Object} field - Field definition from marketplace connection
 * @param {any} value - Current field value
 * @param {Function} t - Translation function
 * @returns {string|null} - Error message or null if valid
 */
export const validateField = (field, value, t) => {
    // Check required fields
    if (field.required && (!value || value.toString().trim() === '')) {
        return t('field_required', { field: field.label || field.name });
    }

    // Skip validation for empty optional fields
    if (!value || value.toString().trim() === '') {
        return null;
    }

    // Type-specific validation
    switch (field.type) {
        case 'Text':
            return validateTextField(field, value, t);
        case 'Password':
            return validatePasswordField(field, value, t);
        case 'MarketplaceOption':
            return validateMarketplaceOptionField(field, value, t);
        case 'OAuthButton':
            return validateOAuthField(field, value, t);
        default:
            return null;
    }
};

/**
 * Validate text field
 * @param {Object} field - Field definition
 * @param {string} value - Field value
 * @param {Function} t - Translation function
 * @returns {string|null} - Error message or null if valid
 */
const validateTextField = (field, value, t) => {
    const strValue = value.toString();

    // Check minimum length
    if (field.minLength && strValue.length < field.minLength) {
        return t('field_min_length', { 
            field: field.label || field.name, 
            length: field.minLength 
        });
    }

    // Check maximum length
    if (field.maxLength && strValue.length > field.maxLength) {
        return t('field_max_length', { 
            field: field.label || field.name, 
            length: field.maxLength 
        });
    }

    // Check regex pattern
    if (field.pattern) {
        const regex = new RegExp(field.pattern);
        if (!regex.test(strValue)) {
            return field.patternErrorMessage || 
                   t('field_invalid_format', { field: field.label || field.name });
        }
    }

    // Check for common format validations
    if (field.format) {
        switch (field.format) {
            case 'email':
                if (!isValidEmail(strValue)) {
                    return t('field_invalid_email');
                }
                break;
            case 'url':
                if (!isValidUrl(strValue)) {
                    return t('field_invalid_url');
                }
                break;
            case 'alphanumeric':
                if (!/^[a-zA-Z0-9]+$/.test(strValue)) {
                    return t('field_alphanumeric_only');
                }
                break;
        }
    }

    return null;
};

/**
 * Validate password field
 * @param {Object} field - Field definition
 * @param {string} value - Field value
 * @param {Function} t - Translation function
 * @returns {string|null} - Error message or null if valid
 */
const validatePasswordField = (field, value, t) => {
    const strValue = value.toString();

    // Basic text validation first
    const textValidation = validateTextField(field, value, t);
    if (textValidation) {
        return textValidation;
    }

    // Password-specific validation
    if (field.minLength && strValue.length < Math.max(field.minLength, 6)) {
        return t('password_min_length', { length: Math.max(field.minLength, 6) });
    }

    if (field.requireUppercase && !/[A-Z]/.test(strValue)) {
        return t('password_require_uppercase');
    }

    if (field.requireLowercase && !/[a-z]/.test(strValue)) {
        return t('password_require_lowercase');
    }

    if (field.requireNumbers && !/\d/.test(strValue)) {
        return t('password_require_numbers');
    }

    if (field.requireSpecialChars && !/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(strValue)) {
        return t('password_require_special_chars');
    }

    return null;
};

/**
 * Validate marketplace option field
 * @param {Object} field - Field definition
 * @param {any} value - Field value
 * @param {Function} t - Translation function
 * @returns {string|null} - Error message or null if valid
 */
const validateMarketplaceOptionField = (field, value, t) => {
    // Handle different valueOptions formats
    let options = [];
    
    if (Array.isArray(field.valueOptions)) {
        // Already an array format
        options = field.valueOptions;
    } else if (field.valueOptions && typeof field.valueOptions === 'object') {
        // Object format like {"The Netherlands": "NL", "Belgium (Dutch)": "BE"}
        // Convert to array format for validation
        options = Object.entries(field.valueOptions).map(([label, val]) => ({
            label,
            value: val
        }));
    }
    
    if (!options || options.length === 0) {
        return t('no_options_available');
    }

    // Check if value exists in options
    const validOption = options.find(option => {
        // Handle different option structures
        if (typeof option === 'string') {
            return option === value;
        }
        return (option.value || option.key || option) === value;
    });

    if (!validOption) {
        return t('invalid_option_selected');
    }

    return null;
};

/**
 * Validate OAuth field
 * @param {Object} field - Field definition
 * @param {any} value - Field value
 * @param {Function} t - Translation function
 * @returns {string|null} - Error message or null if valid
 */
const validateOAuthField = (field, value, t) => {
    if (field.required && !value) {
        return t('oauth_authentication_required');
    }

    // OAuth field is valid if it has any value (indicates successful auth)
    return null;
};

/**
 * Validate all fields in a form
 * @param {Array} fields - Array of field definitions
 * @param {Object} values - Object containing field values
 * @param {Function} t - Translation function
 * @returns {Object} - Object containing field errors
 */
export const validateAllFields = (fields, values, t) => {
    const errors = {};

    fields.forEach(field => {
        const value = values[field.name];
        const error = validateField(field, value, t);
        if (error) {
            errors[field.name] = error;
        }
    });

    return errors;
};

/**
 * Check if there are any validation errors
 * @param {Object} errors - Errors object from validateAllFields
 * @returns {boolean} - True if there are errors
 */
export const hasValidationErrors = (errors) => {
    return Object.keys(errors).length > 0;
};

/**
 * Async validation for connection testing
 * @param {Object} field - Field definition
 * @param {any} value - Field value
 * @param {Object} allValues - All form values for context
 * @param {Function} testConnectionFn - Function to test connection
 * @returns {Promise<string|null>} - Promise resolving to error message or null
 */
export const validateConnectionAsync = async (field, value, allValues, testConnectionFn) => {
    if (!testConnectionFn || !value) {
        return null;
    }

    try {
        const result = await testConnectionFn(field, value, allValues);
        return result.success ? null : result.error || 'Connection test failed';
    } catch (error) {
        console.error('Connection validation error:', error);
        return 'Connection test failed';
    }
};

/**
 * Generate validation error messages
 * @param {string} errorKey - Error message key
 * @param {Object} params - Parameters for error message
 * @param {Function} t - Translation function
 * @returns {string} - Formatted error message
 */
export const getValidationErrorMessage = (errorKey, params = {}, t) => {
    return t(errorKey, params);
};

// Helper validation functions
const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

const isValidUrl = (url) => {
    try {
        new URL(url);
        return true;
    } catch {
        return false;
    }
};

/**
 * Create a validation schema for yup integration
 * @param {Array} fields - Array of field definitions
 * @param {Function} t - Translation function
 * @returns {Object} - Yup validation schema object
 */
export const createValidationSchema = (fields, t) => {
    const schema = {};

    fields.forEach(field => {
        let fieldSchema;

        switch (field.type) {
            case 'Text':
            case 'Password':
                fieldSchema = field.required 
                    ? { required: t('field_required', { field: field.label || field.name }) }
                    : {};
                break;
            case 'MarketplaceOption':
                fieldSchema = field.required 
                    ? { required: t('field_required', { field: field.label || field.name }) }
                    : {};
                break;
            case 'OAuthButton':
                fieldSchema = field.required 
                    ? { required: t('oauth_authentication_required') }
                    : {};
                break;
            default:
                fieldSchema = {};
        }

        if (Object.keys(fieldSchema).length > 0) {
            schema[field.name] = fieldSchema;
        }
    });

    return schema;
};